import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import favouritesService from '../services/favouritesService';
import {handleError} from '../utils/errorHandling';

const FAVOURITES_STORAGE_KEY = 'favourites';

interface FavouritesContextType {
  favouritedIds: Set<string>;
  isFavourited: (artisticObjectId: string) => boolean;
  addToFavourites: (artisticObjectId: string, isAuthenticated: boolean) => Promise<void>;
  removeFromFavourites: (artisticObjectId: string, isAuthenticated: boolean) => Promise<void>;
  toggleFavourite: (artisticObjectId: string, isAuthenticated: boolean) => Promise<void>;
  loadFavourites: (isAuthenticated: boolean) => Promise<void>;
  mergeFavourites: () => Promise<void>;
  clearFavourites: () => void;
}

const FavouritesContext = createContext<FavouritesContextType | undefined>(undefined);

interface FavouritesProviderProps {
  children: ReactNode;
}

export const FavouritesProvider: React.FC<FavouritesProviderProps> = ({ children }) => {
  const [favouritedIds, setFavouritedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadFavouritesFromLocalStorage();
  }, []);

  const loadFavouritesFromLocalStorage = (): void => {
    try {
      const stored = localStorage.getItem(FAVOURITES_STORAGE_KEY);
      if (stored) {
        const ids = JSON.parse(stored) as string[];
        setFavouritedIds(new Set(ids));
      }
    } catch (error) {
      console.error('Error loading favourites from localStorage:', error);
    }
  };

  const saveFavouritesToLocalStorage = (ids: Set<string>): void => {
    try {
      localStorage.setItem(FAVOURITES_STORAGE_KEY, JSON.stringify(Array.from(ids)));
    } catch (error) {
      console.error('Error saving favourites to localStorage:', error);
    }
  };

  const clearFavouritesFromLocalStorage = (): void => {
    try {
      localStorage.removeItem(FAVOURITES_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing favourites from localStorage:', error);
    }
  };

  const loadFavourites = async (isAuthenticated: boolean): Promise<void> => {
    if (!isAuthenticated) {
      loadFavouritesFromLocalStorage();
      return;
    }

    await handleError(
      async () => {
        const ids = await favouritesService.getFavouritedArtisticObjectIds();
        setFavouritedIds(new Set(ids));
      },
      'Error loading favourites:'
    );
  };

  const isFavourited = (artisticObjectId: string): boolean => {
    return favouritedIds.has(artisticObjectId);
  };

  const addToFavourites = async (artisticObjectId: string, isAuthenticated: boolean): Promise<void> => {
    if (!isAuthenticated) {
      setFavouritedIds(prev => {
        const newSet = new Set(prev);
        newSet.add(artisticObjectId);
        saveFavouritesToLocalStorage(newSet);
        return newSet;
      });
      return;
    }

    await handleError(
      async () => {
        await favouritesService.addToFavourites(artisticObjectId);
        setFavouritedIds(prev => {
          const newSet = new Set(prev);
          newSet.add(artisticObjectId);
          return newSet;
        });
      },
      'Error adding to favourites:'
    );
  };

  const removeFromFavourites = async (artisticObjectId: string, isAuthenticated: boolean): Promise<void> => {
    if (!isAuthenticated) {
      setFavouritedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(artisticObjectId);
        saveFavouritesToLocalStorage(newSet);
        return newSet;
      });
      return;
    }

    await handleError(
      async () => {
        await favouritesService.removeFromFavourites(artisticObjectId);
        setFavouritedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(artisticObjectId);
          return newSet;
        });
      },
      'Error removing from favourites:'
    );
  };

  const toggleFavourite = async (artisticObjectId: string, isAuthenticated: boolean): Promise<void> => {
    if (isFavourited(artisticObjectId)) {
      await removeFromFavourites(artisticObjectId, isAuthenticated);
    } else {
      await addToFavourites(artisticObjectId, isAuthenticated);
    }
  };

  const mergeFavourites = async (): Promise<void> => {
    const localFavourites = Array.from(favouritedIds);
    if (localFavourites.length === 0) {
      return;
    }

    await handleError(
      async () => {
        await favouritesService.mergeFavourites(localFavourites);
        clearFavouritesFromLocalStorage();
      },
      'Error merging favourites:'
    );
  };

  const clearFavourites = (): void => {
    setFavouritedIds(new Set());
    clearFavouritesFromLocalStorage();
  };

  const value: FavouritesContextType = {
    favouritedIds,
    isFavourited,
    addToFavourites,
    removeFromFavourites,
    toggleFavourite,
    loadFavourites,
    mergeFavourites,
    clearFavourites,
  };

  return (
    <FavouritesContext.Provider value={value}>
      {children}
    </FavouritesContext.Provider>
  );
};

export const useFavourites = (): FavouritesContextType => {
  const context = useContext(FavouritesContext);
  if (context === undefined) {
    throw new Error('useFavourites must be used within a FavouritesProvider');
  }
  return context;
};

